import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Briefcase,
  Download,
  Eye,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import {
  deleteJob,
  downloadCareerCv,
  fetchAdminJobs,
  fetchCareersPageSettings,
  fetchJobApplications,
  getCareerCvSignedUrl,
  saveCareersPageSettings,
  updateJobApplicationStatus,
  uploadCareerImage,
} from '@/services/careerService';
import { careerCopy, JobApplication, JobApplicationStatus } from '@/types/career';

const CareersManagement = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [viewingApp, setViewingApp] = useState<JobApplication | null>(null);
  const [cvLink, setCvLink] = useState<string>('');
  const [isDownloadingCv, setIsDownloadingCv] = useState(false);

  const { data: jobs = [], isLoading: jobsLoading } = useQuery({
    queryKey: ['admin', 'careers'],
    queryFn: fetchAdminJobs,
  });

  const { data: applications = [], isLoading: appsLoading } = useQuery({
    queryKey: ['admin', 'careers', 'applications'],
    queryFn: () => fetchJobApplications(),
  });

  const { data: settings } = useQuery({
    queryKey: ['admin', 'careers', 'page-settings'],
    queryFn: fetchCareersPageSettings,
  });

  const [heroEyebrowFr, setHeroEyebrowFr] = useState('');
  const [heroEyebrowEn, setHeroEyebrowEn] = useState('');
  const [heroTitleFr, setHeroTitleFr] = useState('');
  const [heroTitleEn, setHeroTitleEn] = useState('');
  const [heroSubtitleFr, setHeroSubtitleFr] = useState('');
  const [heroSubtitleEn, setHeroSubtitleEn] = useState('');
  const [heroBackgroundImage, setHeroBackgroundImage] = useState('');
  useEffect(() => {
    if (!settings) return;
    setHeroEyebrowFr(settings.heroEyebrowFr || '');
    setHeroEyebrowEn(settings.heroEyebrowEn || '');
    setHeroTitleFr(settings.heroTitleFr || '');
    setHeroTitleEn(settings.heroTitleEn || '');
    setHeroSubtitleFr(settings.heroSubtitleFr || '');
    setHeroSubtitleEn(settings.heroSubtitleEn || '');
    setHeroBackgroundImage(settings.heroBackgroundImage || '');
  }, [settings]);

  const newAppsCount = useMemo(
    () => applications.filter((a) => a.status === 'new').length,
    [applications]
  );

  const deleteMutation = useMutation({
    mutationFn: deleteJob,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'careers'] });
      queryClient.invalidateQueries({ queryKey: ['careers'] });
      toast({ title: 'Job deleted' });
    },
    onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: JobApplicationStatus }) =>
      updateJobApplicationStatus(id, status),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'careers', 'applications'] });
      setViewingApp(updated);
      toast({ title: 'Status updated' });
    },
  });

  const saveHeroMutation = useMutation({
    mutationFn: () =>
      saveCareersPageSettings({
        heroEyebrowFr,
        heroEyebrowEn,
        heroTitleFr,
        heroTitleEn,
        heroSubtitleFr,
        heroSubtitleEn,
        heroBackgroundImage,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['careers', 'page-settings'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'careers', 'page-settings'] });
      toast({ title: 'Hero settings saved' });
    },
    onError: () => toast({ title: 'Save failed', variant: 'destructive' }),
  });

  const openApplication = async (app: JobApplication) => {
    setViewingApp(app);
    setCvLink('');
    try {
      const url = await getCareerCvSignedUrl(app.cvUrl);
      setCvLink(url);
    } catch (error) {
      console.error(error);
      setCvLink(app.cvUrl.startsWith('http') ? app.cvUrl : '');
    }
  };

  const handleHeroUpload = async (file: File | null) => {
    if (!file) return;
    try {
      const url = await uploadCareerImage(file);
      setHeroBackgroundImage(url);
      toast({ title: 'Hero image uploaded' });
    } catch (error) {
      console.error(error);
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Careers | Admin</title>
      </Helmet>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Careers</h1>
          <p className="text-sm text-muted-foreground">
            Manage job posts and review applications.
          </p>
        </div>
        <Button asChild className="bg-furniture-gold text-black hover:bg-furniture-gold-hover">
          <Link to="/admin/careers/new">
            <Plus className="mr-2 h-4 w-4" />
            New job
          </Link>
        </Button>
      </div>

      <Tabs defaultValue="jobs">
        <TabsList className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="applications" className="relative">
            Applications
            {newAppsCount > 0 && (
              <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] text-white">
                {newAppsCount}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="hero">Page hero</TabsTrigger>
        </TabsList>

        <TabsContent value="jobs" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Briefcase className="h-4 w-4" /> Open roles
              </CardTitle>
            </CardHeader>
            <CardContent>
              {jobsLoading ? (
                <div className="flex items-center gap-2 py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : jobs.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No jobs yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {jobs.map((job) => (
                        <TableRow key={job.id}>
                          <TableCell className="font-medium">{job.title.fr}</TableCell>
                          <TableCell>{job.employmentType}</TableCell>
                          <TableCell>{job.location}</TableCell>
                          <TableCell>
                            <Badge variant={job.isPublished ? 'default' : 'outline'}>
                              {job.isPublished ? 'Published' : 'Draft'}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/careers/${job.slug}`} target="_blank">
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button asChild variant="ghost" size="sm">
                                <Link to={`/admin/careers/edit/${job.id}`}>
                                  <Pencil className="h-4 w-4" />
                                </Link>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                onClick={() => {
                                  if (confirm('Delete this job?')) deleteMutation.mutate(job.id);
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="applications" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-4 w-4" /> Applications
              </CardTitle>
            </CardHeader>
            <CardContent>
              {appsLoading ? (
                <div className="flex items-center gap-2 py-8 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                </div>
              ) : applications.length === 0 ? (
                <p className="py-8 text-center text-muted-foreground">No applications yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Candidate</TableHead>
                        <TableHead>Job</TableHead>
                        <TableHead>Contact</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">View</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {applications.map((app) => (
                        <TableRow key={app.id}>
                          <TableCell className="font-medium">
                            {app.lastName} {app.firstName}
                          </TableCell>
                          <TableCell>
                            {app.job ? careerCopy(app.job.title, 'fr') : '—'}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div>{app.email}</div>
                            <div className="text-muted-foreground">{app.phone}</div>
                          </TableCell>
                          <TableCell>
                            <Badge
                              variant={
                                app.status === 'new'
                                  ? 'default'
                                  : app.status === 'reviewed'
                                    ? 'secondary'
                                    : 'outline'
                              }
                            >
                              {app.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-sm text-muted-foreground">
                            {new Date(app.createdAt).toLocaleDateString('fr-FR')}
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => openApplication(app)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="hero" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Careers page hero</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Eyebrow FR</Label>
                  <Input value={heroEyebrowFr} onChange={(e) => setHeroEyebrowFr(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Eyebrow EN</Label>
                  <Input value={heroEyebrowEn} onChange={(e) => setHeroEyebrowEn(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Title FR</Label>
                  <Input value={heroTitleFr} onChange={(e) => setHeroTitleFr(e.target.value)} />
                </div>
                <div className="space-y-2">
                  <Label>Title EN</Label>
                  <Input value={heroTitleEn} onChange={(e) => setHeroTitleEn(e.target.value)} />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label>Subtitle FR</Label>
                  <Textarea
                    rows={3}
                    value={heroSubtitleFr}
                    onChange={(e) => setHeroSubtitleFr(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Subtitle EN</Label>
                  <Textarea
                    rows={3}
                    value={heroSubtitleEn}
                    onChange={(e) => setHeroSubtitleEn(e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Background image</Label>
                {heroBackgroundImage ? (
                  <img
                    src={heroBackgroundImage}
                    alt=""
                    className="h-40 w-full rounded-xl object-cover"
                  />
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" asChild>
                    <label className="cursor-pointer">
                      <Upload className="mr-2 h-4 w-4" />
                      Upload
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleHeroUpload(e.target.files?.[0] ?? null)}
                      />
                    </label>
                  </Button>
                  <Input
                    value={heroBackgroundImage}
                    onChange={(e) => setHeroBackgroundImage(e.target.value)}
                    placeholder="Or paste URL"
                  />
                </div>
              </div>
              <Button
                onClick={() => saveHeroMutation.mutate()}
                disabled={saveHeroMutation.isPending}
                className="bg-furniture-gold text-black hover:bg-furniture-gold-hover"
              >
                Save hero
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={Boolean(viewingApp)} onOpenChange={(open) => !open && setViewingApp(null)}>
        <DialogContent className="max-h-[90dvh] max-w-lg overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Application details</DialogTitle>
          </DialogHeader>
          {viewingApp && (
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-muted-foreground">Candidate:</span>{' '}
                <strong>
                  {viewingApp.lastName} {viewingApp.firstName}
                </strong>
              </p>
              <p>
                <span className="text-muted-foreground">Job:</span>{' '}
                {viewingApp.job ? careerCopy(viewingApp.job.title, 'fr') : '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Email:</span> {viewingApp.email}
              </p>
              <p>
                <span className="text-muted-foreground">Phone:</span> {viewingApp.phone}
              </p>
              <p>
                <span className="text-muted-foreground">Extra:</span>{' '}
                {viewingApp.extraInfo || '—'}
              </p>
              <p>
                <span className="text-muted-foreground">Status:</span> {viewingApp.status}
              </p>
              <div className="flex flex-wrap gap-2">
                {cvLink ? (
                  <Button asChild variant="outline" size="sm">
                    <a href={cvLink} target="_blank" rel="noreferrer">
                      Open CV
                    </a>
                  </Button>
                ) : null}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isDownloadingCv || !viewingApp.cvUrl}
                  onClick={async () => {
                    if (!viewingApp) return;
                    setIsDownloadingCv(true);
                    try {
                      await downloadCareerCv(
                        viewingApp.cvUrl,
                        `CV_${viewingApp.lastName}_${viewingApp.firstName}`
                      );
                      toast({ title: 'CV downloaded' });
                    } catch (error) {
                      console.error(error);
                      toast({ title: 'CV download failed', variant: 'destructive' });
                    } finally {
                      setIsDownloadingCv(false);
                    }
                  }}
                >
                  {isDownloadingCv ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Download className="mr-2 h-4 w-4" />
                  )}
                  Download PDF
                </Button>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-row">
            <Button
              variant="outline"
              onClick={() =>
                viewingApp &&
                statusMutation.mutate({ id: viewingApp.id, status: 'reviewed' })
              }
            >
              Mark reviewed
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                viewingApp &&
                statusMutation.mutate({ id: viewingApp.id, status: 'archived' })
              }
            >
              Archive
            </Button>
            <Button onClick={() => setViewingApp(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CareersManagement;
